// The replay of a finished reading: when each recorded field and each check
// appears, on the design's clock (design/ai-bookkeeper-premium.html, readDoc).
//
// What is replayed is the record — the fields the extraction stored, in reading
// order, lit at their recorded spans, and the checks with their recorded
// outcomes. What is NOT the record is the pace: the real reading took as long
// as the model took, and the screen says so beside the replay. Nothing here
// decides what a field's value or a check's outcome is; it only decides when
// each one appears.

import { useEffect, useState } from 'react';

/** The design's timings, in milliseconds. */
export const REPLAY = {
  firstField: 350,
  perField: 340,
  typing: 260,
  afterFields: 200,
  perCheck: 230,
  afterChecks: 200,
  /** The beam's sweep over the page (styles.css `.scan`). */
  beam: 3400,
} as const;

export interface ReplayFrame {
  /** How many fields, in order, have appeared. */
  readonly fields: number;
  /** How many checks have appeared. */
  readonly checks: number;
  /** The proposal summary has faded in. */
  readonly summary: boolean;
  readonly done: boolean;
}

export const FINISHED: ReplayFrame = { fields: Number.POSITIVE_INFINITY, checks: Number.POSITIVE_INFINITY, summary: true, done: true };

/** The frame at `elapsed` ms into a replay of `fieldCount` fields and `checkCount` checks. */
export function frameAt(elapsed: number, fieldCount: number, checkCount: number): ReplayFrame {
  const fields = elapsed < REPLAY.firstField ? 0 : Math.min(fieldCount, Math.floor((elapsed - REPLAY.firstField) / REPLAY.perField) + 1);
  const checksStart = REPLAY.firstField + fieldCount * REPLAY.perField + REPLAY.afterFields;
  const checks = elapsed < checksStart ? 0 : Math.min(checkCount, Math.floor((elapsed - checksStart) / REPLAY.perCheck) + 1);
  const summaryAt = checksStart + checkCount * REPLAY.perCheck + REPLAY.afterChecks;
  const summary = elapsed >= summaryAt;
  return { fields, checks, summary, done: summary && elapsed >= Math.max(summaryAt, REPLAY.beam) };
}

/** Whether the person asked the system for less motion. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * Plays a replay each time `run` changes to a positive number; 0 means none
 * is playing and everything is shown. Under reduced motion nothing is staged:
 * the frame is FINISHED at once.
 */
export function useReplay(run: number, fieldCount: number, checkCount: number): ReplayFrame & { readonly playing: boolean } {
  const [frame, setFrame] = useState<ReplayFrame>(FINISHED);
  useEffect(() => {
    if (run === 0 || prefersReducedMotion()) {
      setFrame(FINISHED);
      return undefined;
    }
    const started = Date.now();
    setFrame(frameAt(0, fieldCount, checkCount));
    const timer = setInterval(() => {
      const next = frameAt(Date.now() - started, fieldCount, checkCount);
      setFrame(next);
      if (next.done) clearInterval(timer);
    }, 40);
    return () => clearInterval(timer);
  }, [run, fieldCount, checkCount]);
  return { ...frame, playing: !frame.done };
}

/**
 * Has this person seen this document's replay in this browser? Per viewer, so
 * a colleague opening the same document gets it too. Storage can be missing or
 * refuse (a private window); then the answer is "yes, seen", so a replay never
 * plays on every visit because a write failed.
 */
export function seenReplay(personId: string, documentId: string): boolean {
  try {
    return window.localStorage.getItem(replayKey(personId, documentId)) !== null;
  } catch {
    return true;
  }
}

export function markReplaySeen(personId: string, documentId: string): void {
  try {
    window.localStorage.setItem(replayKey(personId, documentId), '1');
  } catch {
    // Not remembered; seenReplay already answers "seen" when storage refuses.
  }
}

function replayKey(personId: string, documentId: string): string {
  return `bk.replayed.${personId}.${documentId}`;
}
